import { useMutation } from '@tanstack/react-query';

import { imageToBlurHash } from '@/shared/helpers';
import { extractAndUploadThumbnail, getMediaDuration, uploadAsync } from '@/shared/utilities';
import { type Attachment, AudioAttachment, FileAttachment, GifAttachment, MediaAttachment } from '../models/attachment';
import { attachmentKeys } from './attachment-keys';

export const UPLOAD_FAIL_MESSAGE = 'Tải lên thất bại';

/**
 * Hook để tải lên tệp sử dụng React Query
 * @returns UseMutationResult với mutationFn xử lý upload ManagedFile
 */
export function useAttachmentUpload() {
  return useMutation({
    mutationKey: attachmentKeys.upload,
    mutationFn: async (attachment: Attachment): Promise<Attachment> => {
      try {
        if (!attachment.file) throw new Error('File not found');
        if (attachment.url) return attachment;

        const url = await uploadAsync(attachment.file);
        const f = attachment.file;
        const imageBlur = await imageToBlurHash(f);
        const durationMs = await getMediaDuration(f);
        const { url: thumbnailUrl, width, height } = await extractAndUploadThumbnail(f);
        // Extract waveform cho audio dựa vào sound level

        switch (f.type) {
          case f.type.startsWith('audio/') ? f.type : '':
            return new AudioAttachment(f, { ...attachment, durationMs, url, waveforms: attachment.waveforms });
          case 'image/gif':
            return new GifAttachment(f, { ...attachment, ...imageBlur, url });
          case f.type.startsWith('video/') || f.type.startsWith('image/') ? f.type : '':
            return new MediaAttachment(f, {
              ...attachment,
              ...imageBlur,
              durationMs,
              thumbnailUrl,
              url,
              width: imageBlur.width || width,
              height: imageBlur.height || height
            });
          default:
            return new FileAttachment(f, { ...attachment, url });
        }
      } catch (error) {
        console.error('Upload error for file:', attachment.filename, error);
        return Promise.reject(UPLOAD_FAIL_MESSAGE);
      }
    }
  });
}
